import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Heading } from 'components';
import { formatDownloadOpening } from './downloadOpening';
import {
  CountdownWrapper,
  CountdownIntro,
  CountdownSubtitle,
  CountdownUnits,
  CountdownUnit,
  CountdownValue,
  CountdownLabel,
} from './BookCountdown.styled';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const pad = (value) => String(value).padStart(2, '0');

// Counts down to the moment the timed books can be downloaded, then calls onFinish
// so the list can be fetched again - this time with the files in it.
const BookCountdown = ({ target, onFinish }) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= target) {
        clearInterval(timer);
        onFinish();
      }
    }, SECOND);

    return () => clearInterval(timer);
  }, [target, onFinish]);

  const left = target - now;

  if (left <= 0) {
    return null;
  }

  const units = [
    { key: 'days', value: Math.floor(left / DAY) },
    { key: 'hours', value: Math.floor(left / HOUR) % 24 },
    { key: 'minutes', value: Math.floor(left / MINUTE) % 60 },
    { key: 'seconds', value: Math.floor(left / SECOND) % 60 },
  ];

  return (
    <CountdownWrapper>
      <CountdownIntro>
        <Heading as="h3">{t('page.user.myLibrary.countdown.title')}</Heading>
        <CountdownSubtitle>
          {t('page.user.myLibrary.countdown.subtitle', { date: formatDownloadOpening(target) })}
        </CountdownSubtitle>
      </CountdownIntro>
      <CountdownUnits role="timer">
        {units.map(({ key, value }) => (
          <CountdownUnit key={key}>
            <CountdownValue>{pad(value)}</CountdownValue>
            <CountdownLabel>{t(`page.user.myLibrary.countdown.${key}`)}</CountdownLabel>
          </CountdownUnit>
        ))}
      </CountdownUnits>
    </CountdownWrapper>
  );
};

BookCountdown.propTypes = {
  target: PropTypes.number.isRequired,
  onFinish: PropTypes.func,
};

BookCountdown.defaultProps = {
  onFinish: () => {},
};

export default BookCountdown;
